/**
 * Ensures every dynamically-created <style> element carries the CSP nonce.
 *
 * Ant Design's cssinjs styles already receive the nonce via <ConfigProvider csp>,
 * but some transitive dependencies — notably rc-util's `updateCSS` (used by antd's
 * scroll-lock for Modal/Drawer/Select) — inject <style> elements WITHOUT threading
 * that nonce. Under a strict `style-src 'self' 'nonce-…'` (no 'unsafe-inline'),
 * those styles are blocked, surfacing as:
 *
 *   "Applying inline style violates the following Content Security Policy directive
 *    'style-src 'self' 'nonce-…''. Either the 'unsafe-inline' keyword, a hash
 *    ('sha256-47DEQpj8HBSa+/TImW+5JCeuQeRkm5NMpJWZG3hSuFU='), or a nonce ... "
 *
 * (The empty-string hash appears when the injected style's content is momentarily
 * empty, e.g. as the scroll-lock toggles off.)
 *
 * Stamping the nonce at element-creation time authorizes these styles WITHOUT
 * adding 'unsafe-inline' or 'unsafe-hashes'. Must run before any dependency
 * creates a <style> (i.e. before React renders).
 */
export function installStyleNonce(nonce: string | undefined): void {
  if (!nonce || typeof document === 'undefined') return;
  const doc = document as Document & { __styleNoncePatched?: boolean };
  if (doc.__styleNoncePatched) return;
  doc.__styleNoncePatched = true;

  const originalCreateElement = document.createElement.bind(document);
  (document as { createElement: unknown }).createElement = function (
    tagName: string,
    options?: ElementCreationOptions,
  ): HTMLElement {
    const el = originalCreateElement(tagName, options);
    if (typeof tagName === 'string' && tagName.toLowerCase() === 'style') {
      const style = el as HTMLStyleElement;
      if (!style.getAttribute('nonce')) style.setAttribute('nonce', nonce);
      // The IDL `.nonce` property is what CSP checks after the parser hides the
      // content attribute, so set both.
      try {
        style.nonce = nonce;
      } catch {
        /* some engines expose `nonce` as read-only; the attribute is enough */
      }
    }
    return el;
  };
}
